
import { Options, Vue } from "vue-class-component";
import { ApiRequestList, OptionsClass } from "@/domain/entities/MainApp";
import copy from "copy-text-to-clipboard";
import {
  INTEGRATION_PLATFORMS,
  INTEGRATION_PLATFORM_ENGLISH_COPY
} from "./constant";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { ChipsStyles } from "../../components/chips-v2/modules";
import ModalAddNewToken from "./components/modal-add-new-token.vue";
import { MiddlewareController } from "../../controllers/MiddlewareController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { IntegrationApi } from "@/domain/entities/Integration";
import Notification from "@/app/ui/components/notification/index.vue";
import { MainAppController } from "../../controllers/MainAppController";

@Options({
  components: {
    ModalAddNewToken,
    Notification
  }
})
class Integration extends Vue {
  get pageTitle() {
    return "API Integrate";
  }

  get platformSectionTitle() {
    return "Select platform to integrate";
  }

  get connectedStoreLabel() {
    return "stores integrated";
  }

  get comingSoonLabel() {
    return "Coming soon";
  }

  get addTokenLabel() {
    return "Add token";
  }

  get connectLabel() {
    return "Integrate";
  }

  get emptyHeaderLabel() {
    return "No platforms connected yet";
  }

  get emptyMessageLabel() {
    return "Select a platform and add a token to connect it";
  }

  get platformList() {
    return INTEGRATION_PLATFORMS.map((platform) => ({
      ...platform,
      ...INTEGRATION_PLATFORM_ENGLISH_COPY[platform.value]
    }));
  }

  openModalAddToken = false;

  async getSummary(platform: string) {
    try {
      const res = await MiddlewareController.getListIntegrationToken(
        new ApiRequestList({
          page: this.listData.pagination.page,
          limit: this.listData.pagination.limit,
          platform
        })
      );
      return res.pagination.totalData;
    } catch (err) {
      return 0;
    }
  }
  async onGetSummary() {
    const platforms = INTEGRATION_PLATFORMS.filter((e) => !e.disabled);

    await Promise.all(
      platforms.map(async (item) => {
        const total = await this.getSummary(item.value);

        this.platformList.forEach((e) => {
          if (e.value === item.value && e.meta) {
            e.meta.totalConnected = total;
          }
        });
      })
    );
  }

  listData = new ResponsePayloadV2();
  async fetchList() {
    try {
      this.listData.loading = true;
      this.listData = await MiddlewareController.getListIntegrationToken(
        new ApiRequestList({
          page: this.listData.pagination.page,
          limit: this.listData.pagination.limit
        })
      );
    } catch (err) {
      this.listData.message = parsingErrorResponse(err).type;
    } finally {
      this.listData.loading = false;
    }
  }

  get columns() {
    return [
      {
        name: "No",
        styleHead: "w-16 text-left whitespace-no-wrap align-middle break-all",
        render: (_: IntegrationApi, index: number) =>
          (this.listData.pagination.page - 1) * this.listData.pagination.limit +
          index +
          1
      },
      {
        name: "Store Name",
        styleHead:
          "w-44 text-left whitespace-no-wrap align-middle break-all min-w-0",
        render: (item: IntegrationApi) =>
          `<div class="truncate capitalize text-left font-medium">
              ${item.appName}
            </div>`
      },
      {
        name: "Platform",
        styleHead:
          "w-32 text-left whitespace-no-wrap align-middle break-all min-w-0",
        render: (item: IntegrationApi) =>
          `<div class="truncate capitalize text-left font-medium">
              ${item.platform}
            </div>`
      },
      {
        name: "Domain",
        styleHead: "w-60 text-left whitespace-no-wrap align-middle break-all",
        render: (item: IntegrationApi) =>
          `<div class="overflow-ellipsis text-left flex flex-row gap-x-1 font-medium">
              ${item.domain?.toLowerCase() || "-"}
            </div>`
      },
      {
        name: "Client ID",
        styleHead: "w-52 text-left whitespace-no-wrap align-middle break-all",
        render: (item: IntegrationApi) =>
          `<div class="capitalize overflow-ellipsis text-left flex flex-row gap-x-1 font-medium">
              ${this.maskValue(item.clientKey)}
            </div>`,
        key: "actionable_column",
        showButton: () => true,
        icon: "copy-clipboard",
        iconStyle: "ml-1",
        iconColor: "red-lp-100",
        onClickValue: this.copyClientId
      },
      {
        name: "Client Secret",
        styleHead: "w-52 text-left whitespace-no-wrap align-middle break-all",
        render: (item: IntegrationApi) =>
          `<div class="capitalize overflow-ellipsis text-left flex flex-row gap-x-1 font-medium">
              ${this.maskValue(item.clientSecret)}
            </div>`,
        key: "actionable_column",
        showButton: () => true,
        icon: "copy-clipboard",
        iconStyle: "ml-1",
        iconColor: "red-lp-100",
        onClickValue: this.copyClientSecreet
      },
      {
        name: "API Status",
        styleHead: "w-40 text-left whitespace-no-wrap align-middle break-all",
        render: ({ status }: IntegrationApi) => this.renderStatus(status)
      }
    ];
  }

  renderStatus(status: string) {
    const key = status.toLowerCase();
    const title =
      key === "active" ? "Active" : key === "inactive" ? "Nonactive" : status;

    return ChipsStyles({ status, title });
  }

  async onUpdateList() {
    MainAppController.showLoading("Processing");
    await Promise.all([this.fetchList(), this.onGetSummary()]);
    MainAppController.closeLoading();
  }

  // OLD DATA
  mounted() {
    this.onUpdateList();
  }

  wordingCopied = "";
  notification = false;
  setNotification() {
    this.notification = false;

    setTimeout(() => {
      this.notification = true;
    }, 10);
  }

  get copyStyles() {
    return {
      background: "bg-gray-lp-300",
      text: "text-white",
      message: this.wordingCopied,
      width: "w-300px"
    };
  }

  copyClientId(item: IntegrationApi) {
    if (item.clientKey && item.status !== "expired") {
      this.wordingCopied = "Client ID copied";
      const result = copy(item.clientKey);
      if (!result) return;

      this.setNotification();
    }
  }

  copyClientSecreet(item: IntegrationApi) {
    if (item.clientKey && item.status !== "expired") {
      this.wordingCopied = "Client Secret copied";
      const result = copy(item.clientSecret);
      if (!result) return;

      this.setNotification();
    }
  }

  maskValue(value: string): string {
    if (!value) return "";

    const first = value.slice(0, 5);
    const last = value.slice(-5);

    return `${first}*****${last}`;
  }

  platformSelected = new OptionsClass();
  onAddNewToken(item: OptionsClass) {
    this.platformSelected = item;
    this.openModalAddToken = true;
  }
}
export default Integration;
